// appeal site
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './style.css';

axios.defaults.withCredentials = true;

export const API_URL = process.env.REACT_APP_API_URL || "https://api.appeal.mhadiscord.com";

console.log('[APPEAL] Initializing with API_URL:', API_URL);

interface AuthedUser {
    id: string;
    username: string;
    avatar?: string | null;
}

type AppealStatus = 'open' | 'accepted' | 'rejected' | 'info_requested';

interface RequestMessage {
    id: string;
    fromStaff: boolean;
    message: string;
    createdAt: string;
}

interface Appeal {
    id: string;
    discordId: string;
    username: string;
    email: string | null;
    banDate: string | null;
    banReason: string;
    understandingObjection: string;
    ifUnbanned: string;
    additionalComments: string;
    status: AppealStatus;
    submittedAt: string;
    updatedAt: string;
    resolutionComments: string;
    requestMessages?: RequestMessage[];
}

interface StatusResponse {
    user: AuthedUser;
    banned: boolean | undefined;
    banReason: string | undefined;
    blacklisted: boolean;
    blacklistMessage: string | null;  // raw template, may contain {timeframe}
    blacklistExpiresAt: string | null; // UTC ISO string
    hasAppeal: boolean;
    appeal: Appeal | null;
}

const App: React.FC = () => {
    const [user, setUser] = useState<AuthedUser | null>(null);
    const [status, setStatus] = useState<StatusResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [statusLoading, setStatusLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [banDate, setBanDate] = useState('');
    const [banReason, setBanReason] = useState('');
    const [understandingObjection, setUnderstandingObjection] = useState('');
    const [ifUnbanned, setIfUnbanned] = useState('');
    const [additionalComments, setAdditionalComments] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [redirecting, setRedirecting] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');

    // Info-request reply state
    const [replyText, setReplyText] = useState('');
    const [replySubmitting, setReplySubmitting] = useState(false);
    const [replyError, setReplyError] = useState<string | null>(null);
    const [replySuccess, setReplySuccess] = useState<string | null>(null);
    const [requestHistoryExpanded, setRequestHistoryExpanded] = useState(false);

    useEffect(() => {
        console.log('[APPEAL] App mounted, checking authentication');
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('[APPEAL] Making auth check request to:', `${API_URL}/api/auth/user/appeal`);
            const res = await axios.get<{ user: AuthedUser | null }>(`${API_URL}/api/auth/user/appeal`);

            console.log('[APPEAL] Auth check response:', res.data);

            if (!res.data || !res.data.user) {
                console.log('[APPEAL] User not logged in, redirecting to Discord...');
                setRedirecting(true);
                window.location.href = `${API_URL}/api/auth/discord-appeal`;
            } else {
                console.log('[APPEAL] User authenticated:', res.data.user.username);
                setUser(res.data.user);
            }
        } catch (err: any) {
            console.error('[APPEAL] Error during auth check:', err);

            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                console.log('[APPEAL] User not authenticated (401/403), redirecting to Discord...');
                setRedirecting(true);
                window.location.href = `${API_URL}/api/auth/discord-appeal`;
            } else {
                console.error('[APPEAL] Network or other error:', err.message);
                setError('Failed to check authentication.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) {
            console.log('[APPEAL] No user, skipping status load');
            return;
        }
        console.log('[APPEAL] User exists, loading appeal status');
        loadStatus();
    }, [user]);

    const loadStatus = async () => {
        try {
            setStatusLoading(true);
            setError(null);

            console.log('[APPEAL] Loading appeal status from:', `${API_URL}/api/appeals/status`);
            const res = await axios.get<StatusResponse>(`${API_URL}/api/appeals/status`);
            console.log('[APPEAL] Status loaded:', res.data);

            if (res.data.banned === undefined || res.data.banned === null) {
                console.error('[APPEAL] Banned status is undefined/null');
                setError('Unable to determine your ban status. Please contact an administrator.');
                setStatus(null);
            } else {
                setStatus(res.data);
            }

            if (res.data.banReason !== undefined && res.data.banReason !== null && res.data.banReason !== "No reason provided") {
                setBanReason(res.data.banReason);
            }

            if (res.data.appeal?.email) {
                setEmailAddress(res.data.appeal.email);
            }

            // Reset reply state on reload
            setReplyText('');
            setReplyError(null);
            setReplySuccess(null);
            setRequestHistoryExpanded(false);
        } catch (err: any) {
            console.error('[APPEAL] Failed to load status:', err);
            setError('Failed to load ban/appeal status.');
        } finally {
            setStatusLoading(false);
        }
    };

    const formatDateTime = (value: string | null): string => {
        if (!value) return 'N/A';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString('en-GB');
    };

    const handleLogout = async () => {
        console.log('[PANEL] Logging out...');
        try {
            await axios.get(`${API_URL}/api/auth/logout`);
            console.log('[PANEL] Logout successful');
            setUser(null);
        } catch (err) {
            console.error('[PANEL] Failed to logout:', err);
        }
    };

    const handleLogin = () => {
        console.log('[APPEAL] Redirecting to Discord login...');
        setRedirecting(true);
        window.location.href = `${API_URL}/api/auth/discord-appeal`;
    };

    const handleSubmitAppeal = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        console.log('[APPEAL] Submitting appeal form');
        setError(null);
        setSuccess(null);

        if (!banReason.trim()) {
            console.warn('[APPEAL] Ban reason is empty');
            setError('Please provide a reason for your ban.');
            return;
        }

        if (!banned) {
            setError('You cannot submit this form as you are not banned.');
            return;
        }

        try {
            setSubmitting(true);

            const appealData = {
                banDate: banDate || null,
                banReason: banReason.trim(),
                understandingObjection: understandingObjection.trim(),
                ifUnbanned: ifUnbanned.trim(),
                additionalComments: additionalComments.trim(),
                email: emailAddress.trim() || null,
            };

            console.log('[APPEAL] Submitting appeal data:', appealData);

            await axios.post(`${API_URL}/api/appeals`, appealData);

            console.log('[APPEAL] Appeal submitted successfully');
            setSuccess('Your appeal has been submitted successfully.');

            await loadStatus();
        } catch (err: any) {
            console.error('[APPEAL] Failed to submit appeal:', err);
            const msg = err.response?.data?.error || 'Failed to submit your appeal. Please try again later.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitReply = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!latestAppeal || !user) return;

        setReplyError(null);
        setReplySuccess(null);

        if (!replyText.trim()) {
            setReplyError('Please write a reply before submitting.');
            return;
        }

        if (replyText.trim().length > 2000) {
            setReplyError('Your reply must be 2000 characters or fewer.');
            return;
        }

        try {
            setReplySubmitting(true);
            await axios.post(`${API_URL}/api/appeals/${latestAppeal.id}/info-reply`, {
                message: replyText.trim(),
            });
            setReplySuccess('Your reply has been sent. The status has returned to Open while staff review your response.');
            setReplyText('');
            await loadStatus();
        } catch (err: any) {
            console.error('[APPEAL] Failed to submit reply:', err);
            setReplyError(err.response?.data?.error || 'Failed to submit your reply. Please try again.');
        } finally {
            setReplySubmitting(false);
        }
    };

    if (loading && !!redirecting) {
        return (
            <div className="app">
                <div className="loading-container">
                    <div className="loading-spinner" />
                    <p className="loading-text">Loading...</p>
                </div>
            </div>
        );
    }

    if (redirecting) {
        return (
            <div className="app">
                <div className="loading-container">
                    <div className="loading-spinner" />
                    <p className="loading-text">Redirecting to Discord...</p>
                </div>
            </div>
        );
    }

    const banned = status?.banned ?? false;
    const blacklisted = status?.blacklisted ?? false;
    const blacklistExpiresAt = status?.blacklistExpiresAt ?? null;

    // Render the blacklist message: substitute {timeframe} with a local-timezone date
    // For permanent entries expiresAt is null, so {timeframe} stays as "permanently"
    const blacklistMessage = (() => {
        if (!status?.blacklistMessage) return null;
        if (!blacklistExpiresAt) {
            // permanent — message already contains "permanently" as the timeframe
            return status.blacklistMessage.replace('{timeframe}', 'permanently');
        }
        const localDate = new Date(blacklistExpiresAt).toLocaleDateString(undefined, {
            day: 'numeric', month: 'long', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
            timeZoneName: 'short',
        });
        return status.blacklistMessage.replace('{timeframe}', `until ${localDate}`);
    })();
    const hasAppeal = status?.hasAppeal ?? false;
    const latestAppeal = status?.appeal ?? null;

    let message: string | null = null;
    let showForm = false;
    let showReadOnly = false;

    const latestStatus = latestAppeal?.status ?? null;

    if (!user) {
        message = 'You are not logged in...';
    } else if (blacklisted) {
        message = null; // banner handles the message
        showForm = false;
    } else if (statusLoading) {
        message = 'Loading your appeal status...';
    } else if (!status) {
        message = 'Unable to load ban status. Please try refreshing the page.';
    } else {
        if (!banned) {
            if (!latestAppeal) {
                message = 'You are not currently banned!';
            } else if (latestStatus === 'accepted') {
                message = 'Your appeal was accepted and you have been unbanned.';
            } else if (latestStatus === 'rejected') {
                message = 'Your appeal was rejected, but you are currently not banned.';
            } else {
                message = 'You are not currently banned!';
            }
            showForm = false;
        } else {
            if (!latestAppeal) {
                message = 'You are currently banned. You may appeal the ban below.';
                showForm = true;
            } else if (latestStatus === 'open') {
                message = 'You already have a pending appeal. Please wait for a response.';
                showForm = false;
                showReadOnly = true;
            } else if (latestStatus === 'info_requested') {
                message = 'The moderation team has requested further information on your appeal. Please see the message below and reply.';
                showForm = false;
                showReadOnly = true;
            } else if (latestStatus === 'accepted') {
                message = 'Your previous appeal was accepted, but you are currently banned again. You may submit a new appeal below.';
                showForm = true;
            } else if (latestStatus === 'rejected') {
                message = 'Your appeal was rejected and you are still banned. You may submit a new appeal below.';
                showForm = true;
            } else {
                message = 'You have already submitted an appeal.';
                showForm = false;
            }
        }
    }

    if (user?.id === '135040933916966921' || user?.id === '520156958343430144') {
        if (!blacklisted) showForm = true;
    }

    console.log('[APPEAL] Render state - User:', user?.username, 'StatusLoading:', statusLoading, 'Banned:', banned, 'HasAppeal:', hasAppeal, 'ShowForm:', showForm);

    // ─── Request message thread helpers ──────────────────────────────────────

    const renderRequestThread = () => {
        if (!latestAppeal) return null;
        const msgs = latestAppeal.requestMessages ?? [];
        if (msgs.length === 0) return null;

        const lastStaff = [...msgs].reverse().find(m => m.fromStaff);
        const lastUser = [...msgs].reverse().find(m => !m.fromStaff);

        const pinnedMessages: RequestMessage[] = [];
        if (lastStaff) pinnedMessages.push(lastStaff);
        if (lastUser && lastUser.id !== lastStaff?.id) pinnedMessages.push(lastUser);
        pinnedMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

        const hiddenCount = msgs.length - pinnedMessages.length;

        return (
            <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                    Message Thread
                </h3>

                {hiddenCount > 0 && (
                    <button
                        onClick={() => setRequestHistoryExpanded(e => !e)}
                        style={{
                            background: 'none',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: 'inherit',
                            borderRadius: 6,
                            padding: '0.3rem 0.75rem',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            marginBottom: '0.75rem',
                            opacity: 0.7,
                        }}
                    >
                        {requestHistoryExpanded
                            ? `▲ Hide older messages`
                            : `▼ Show ${hiddenCount} older message${hiddenCount !== 1 ? 's' : ''}`}
                    </button>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {requestHistoryExpanded && msgs.slice(0, msgs.length - pinnedMessages.length).map(msg => (
                        <PublicMessageBubble
                            key={msg.id}
                            msg={msg}
                            username={latestAppeal.username}
                            dimmed
                        />
                    ))}
                    {pinnedMessages.map(msg => (
                        <PublicMessageBubble
                            key={msg.id}
                            msg={msg}
                            username={latestAppeal.username}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="app">
            {/* HEADER */}
            <header className="header combined-header">
                <nav className="header-nav"><ul /></nav>

                <div className="header-logo">
                    <img
                        src="images/MHA-Logo.png"
                        alt="HeroNetwork Logo"
                        className="logo-img"
                        style={{ maxHeight: 60 }}
                    />
                </div>

                <div className="header-right">
                    {user && (
                        <button onClick={handleLogout} className="logout-btn">
                            Logout
                        </button>
                    )}
                </div>
            </header>

            <main className="content">
                {error && (
                    <div className="error-message" style={{ maxWidth: 600, margin: '0 auto 1.5rem' }}>
                        {error}
                    </div>
                )}

                {success && (
                    <div style={{
                        maxWidth: 600,
                        margin: '0 auto 1.5rem',
                        padding: '0.75rem 1rem',
                        borderRadius: 6,
                        backgroundColor: 'rgba(0,128,0,0.08)',
                        color: '#00e676',
                    }}>
                        {success}
                    </div>
                )}

                {/* Main message & current status */}
                <section className="section" style={{ maxWidth: 800, margin: '0 auto 1.5rem' }}>
                    <h1>Unban Appeal</h1>

                    {user ? <p>Welcome, {user?.username}!</p> : <p>Welcome!</p>}

                    {statusLoading && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <div className="loading-spinner" style={{ width: 20, height: 20 }} />
                            <p style={{ opacity: 0.85, margin: 0 }}>Loading your appeal status...</p>
                        </div>
                    )}

                    {blacklisted && blacklistMessage && !statusLoading && (
                        <div className="blacklist-blocked-banner" style={{ marginTop: '1rem' }}>
                            <h3>Appeal Submissions Unavailable</h3>
                            <p>{blacklistMessage}</p>
                        </div>
                    )}

                    {message && !statusLoading && (
                        <p style={{ opacity: 0.85, marginTop: '0.5rem' }}>{message}</p>
                    )}

                    {!user && !redirecting && (
                        <button onClick={handleLogin} className="primary-btn">
                            Login with Discord
                        </button>
                    )}

                    {latestAppeal && !statusLoading && (
                        <div style={{
                            marginTop: '1rem',
                            padding: '0.75rem 1rem',
                            borderRadius: 8,
                            background: latestStatus === 'info_requested'
                                ? 'rgba(88, 101, 242, 0.08)'
                                : 'rgba(255,255,255,0.02)',
                            border: latestStatus === 'info_requested'
                                ? '1px solid rgba(88, 101, 242, 0.4)'
                                : '1px solid rgba(255,255,255,0.08)',
                        }}>
                            <div style={{ marginBottom: 4 }}>
                                <strong>Latest appeal status:</strong>{' '}
                                {latestAppeal.status === 'open'
                                    ? 'Open'
                                    : latestAppeal.status === 'accepted'
                                        ? 'Accepted'
                                        : latestAppeal.status === 'rejected'
                                            ? 'Rejected'
                                            : latestAppeal.status === 'info_requested'
                                                ? '🔵 Further Information Requested'
                                                : 'Open'}
                            </div>
                            <div>
                                <strong>Submitted:</strong>{' '}
                                {formatDateTime(latestAppeal.submittedAt)}
                            </div>
                            {latestAppeal.resolutionComments && (
                                <div style={{ marginTop: 4 }}>
                                    <strong>Resolution comments:</strong>
                                    <div style={{ marginTop: 4 }}>{latestAppeal.resolutionComments}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {!banned && (
                        <div style={{
                            marginTop: '1rem',
                            padding: '0.75rem 1rem',
                            borderRadius: 8,
                            background: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                            <div style={{ marginBottom: 4 }}>
                                <strong>If you haven't already, you can rejoin the server{' '}
                                    <a href="https://discord.com/invite/myheroacademia" target="_blank" style={{ color: 'var(--accent)' }}>here</a>
                                </strong>
                            </div>
                        </div>
                    )}

                    {latestAppeal && latestAppeal.status === 'open' && !statusLoading && (
                        <div style={{
                            marginTop: '1rem',
                            padding: '0.75rem 1rem',
                            borderRadius: 8,
                            background: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                            <div style={{ marginBottom: 4 }}>
                                <strong>You can check this dashboard periodically for any updates on your appeal. Currently, email notifications are not supported.</strong>
                            </div>
                        </div>
                    )}
                </section>

                {/* ── Info Requested section ─────────────────────────────────── */}
                {latestAppeal && (latestAppeal.requestMessages ?? []).length > 0 && latestStatus !== 'accepted' && !statusLoading && (
                    <section className="section" style={{ maxWidth: 800, margin: '0 auto 1.5rem' }}>

                        {latestStatus === 'info_requested' && (
                            <>
                                <h2 style={{ marginBottom: '0.5rem', color: 'var(--accent)' }}>
                                    🔵 Further Information Requested
                                </h2>
                                <p style={{ opacity: 0.8, marginBottom: '1.5rem' }}>
                                    The moderation team needs some more information before they can make a decision on your appeal.
                                    Please read the message(s) below and submit your reply. Once you reply, your appeal will return to <strong>Open</strong> status & you will not be able to add another reply.
                                </p>
                            </>
                        )}

                        {renderRequestThread()}

                        {/* Reply form */}
                        {latestStatus === 'info_requested' && (
                            // reply form here
                            <form onSubmit={handleSubmitReply} style={{ marginTop: '1.5rem' }}>
                                <div className="appeals-field">
                                    <label className="form-label">Your Reply *</label>
                                    <textarea
                                        value={replyText}
                                        onChange={e => setReplyText(e.target.value)}
                                        rows={5}
                                        required
                                        placeholder="Type your reply to the moderation team here..."
                                        maxLength={2000}
                                    />
                                    <small style={{ opacity: 0.5, fontSize: '0.75rem' }}>
                                        {replyText.length} / 2000 characters
                                    </small>
                                </div>

                                {replyError && (
                                    <div className="error-message" style={{ marginBottom: '1rem' }}>
                                        {replyError}
                                    </div>
                                )}

                                {replySuccess && (
                                    <div style={{
                                        padding: '0.75rem 1rem',
                                        borderRadius: 6,
                                        backgroundColor: 'rgba(0,128,0,0.08)',
                                        color: '#00e676',
                                        marginBottom: '1rem',
                                    }}>
                                        {replySuccess}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={replySubmitting}
                                    className="primary-btn"
                                >
                                    {replySubmitting ? 'Submitting...' : 'Submit Reply'}
                                </button>
                            </form>
                        )}
                    </section>
                )}

                {/* ── Read-only submitted appeal ─────────────────────────────── */}
                {showReadOnly && latestAppeal && latestStatus !== 'info_requested' && !statusLoading && (
                    <section className="section" style={{ maxWidth: 800, margin: '0 auto 1.5rem' }}>
                        <h2 style={{ marginBottom: '1.5rem', color: 'var(--accent)' }}>Submitted Appeal</h2>
                        <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
                            This is the appeal you submitted. You can't edit it while it's pending, but
                            you can check this page for the outcome.
                        </p>

                        <div className="appeals-field">
                            <label className="form-label">Discord ID</label>
                            <input type="text" value={latestAppeal.discordId} disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                        </div>

                        <div className="appeals-field">
                            <label className="form-label">Username</label>
                            <input type="text" value={latestAppeal.username} disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                        </div>

                        <div className="appeals-field">
                            <label className="form-label">Email for notifications (optional)</label>
                            <input type="email" value={latestAppeal.email ?? ''} disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                        </div>

                        <div className="appeals-field">
                            <label className="form-label">Ban Reason</label>
                            <textarea value={latestAppeal.banReason} rows={4} disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                        </div>

                        <div className="appeals-field">
                            <label className="form-label">Ban Date</label>
                            <input
                                type="text"
                                value={latestAppeal.banDate ? formatDateTime(latestAppeal.banDate) : 'Not specified'}
                                disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }}
                            />
                        </div>

                        <div className="appeals-field">
                            <label className="form-label">Understanding / Objection</label>
                            <textarea value={latestAppeal.understandingObjection} rows={4} disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                        </div>

                        <div className="appeals-field">
                            <label className="form-label">If I get unbanned...</label>
                            <textarea value={latestAppeal.ifUnbanned} rows={4} disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                        </div>

                        <div className="appeals-field">
                            <label className="form-label">Additional Comments</label>
                            <textarea value={latestAppeal.additionalComments} rows={4} disabled readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                        </div>
                    </section>
                )}

                {/* ── New appeal form ────────────────────────────────────────── */}
                {showForm && !statusLoading && (
                    <section className="section" style={{ maxWidth: 800, margin: '0 auto' }}>
                        <h2 style={{ marginBottom: '1.5rem', color: 'var(--accent)' }}>Appeal Form</h2>
                        <form onSubmit={handleSubmitAppeal}>
                            <div className="appeals-field">
                                <label className="form-label">Discord ID</label>
                                <input type="text" value={user?.id ?? ''} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                            </div>

                            <div className="appeals-field">
                                <label className="form-label">Username</label>
                                <input
                                    type="text"
                                    value={user ? `${user.username}` : ''}
                                    disabled
                                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                />
                            </div>

                            <div className="appeals-field">
                                <label className="form-label">
                                    Ban Reason {status?.banReason && status.banReason !== "No reason provided" ? "(auto-populated)" : "(what were you banned for?) *"}
                                </label>
                                <textarea
                                    value={banReason}
                                    onChange={e => setBanReason(e.target.value)}
                                    rows={4}
                                    required
                                    disabled={status?.banReason && status.banReason !== "No reason provided" ? true : false}
                                    placeholder="Copy/paste the explanation you received right before your ban. Optionally upload a screenshot to imgur.com and paste the link here."
                                    style={status?.banReason && status.banReason !== "No reason provided" ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                />
                            </div>

                            <div className="appeals-field">
                                <label className="form-label">Ban Date (if known)</label>
                                <input type="date" value={banDate} onChange={e => setBanDate(e.target.value)} />
                            </div>

                            <div className="appeals-field">
                                <label className="form-label">Understanding / Objection *</label>
                                <textarea
                                    value={understandingObjection}
                                    onChange={e => setUnderstandingObjection(e.target.value)}
                                    rows={4}
                                    placeholder="Based on the ban reason you got and the rules you were accused of breaking, explain rule by rule your understanding of how you broke or did not break said rules. Example: 'Rule #1 - the content I posted was NSFW; Rule #3 - I understand that I offended that person...,' or 'Rule #1 - that was not NSFW; Rule #3 - I did not offend that person...'"
                                />
                            </div>

                            <div className="appeals-field">
                                <label className="form-label">If I get unbanned... *</label>
                                <textarea
                                    value={ifUnbanned}
                                    onChange={e => setIfUnbanned(e.target.value)}
                                    rows={4}
                                    required
                                    placeholder="Explain in a few words what you are going to change in your behavior (if necessary) if you get unbanned."
                                />
                            </div>

                            <div className="appeals-field">
                                <label className="form-label">Additional Comments</label>
                                <textarea
                                    value={additionalComments}
                                    onChange={e => setAdditionalComments(e.target.value)}
                                    rows={4}
                                    placeholder="Anything else you may like to add (optional)."
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="primary-btn"
                                style={{ marginTop: '1rem' }}
                            >
                                {submitting ? 'Submitting...' : 'Submit Appeal'}
                            </button>
                        </form>
                    </section>
                )}
            </main>

            <footer className="footer">
                <div className="footer-columns">
                    <div className="footer-col footer-left">
                        <img src="images/MHA-Logo.png" alt="HeroNetwork Logo" className="footer-logo" />
                    </div>
                    <div className="footer-col footer-middle">{ }</div>
                    <div className="footer-col footer-right">
                        <span className="subtitle footer-alt">My Hero Academia Links</span>
                        <ul className="link-list align-right">
                            <li className="link"><a href="https://discord.gg/myheroacademia" target="_blank">Discord<i className="bi bi-link-45deg" /></a></li>
                            <li className="link"><a href="https://myheroacademia.fandom.com/wiki/My_Hero_Academia_Wiki" target="_blank">Fandom Wiki<i className="bi bi-link-45deg" /></a></li>
                            <li className="link"><a href="https://aminoapps.com/c/my-hero-academia/" target="_blank">Amino<i className="bi bi-link-45deg" /></a></li>
                            <li className="link"><a href="https://www.crunchyroll.com/series/G6NQ5DWZ6/my-hero-academia" target="_blank">Crunchyroll<i className="bi bi-link-45deg" /></a></li>
                            <li className="link"><a href="https://mangaplus.shueisha.co.jp/titles/100017" target="_blank">Manga<i className="bi bi-link-45deg" /></a></li>
                            <li className="link"><a href="https://forms.gle/g7Wpu4hF6dDuTxL86" target="_blank">DMCA Requests<i className="bi bi-link-45deg" /></a></li>
                        </ul>
                    </div>
                    <div className="footer-copy">© {new Date().getFullYear()} HeroNetwork Appeals</div>
                </div>
            </footer>
        </div>
    );
};

// ── Public-facing message bubble ─────────────────────────────────────────────

interface PublicMessageBubbleProps {
    msg: RequestMessage;
    username: string;
    dimmed?: boolean;
}

const PublicMessageBubble: React.FC<PublicMessageBubbleProps> = ({ msg, username, dimmed = false }) => {
    const formatTime = (value: string) => {
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleString('en-GB');
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: msg.fromStaff ? 'flex-start' : 'flex-end',
            opacity: dimmed ? 0.55 : 1,
        }}>
            <div style={{
                maxWidth: '85%',
                padding: '0.6rem 0.85rem',
                borderRadius: msg.fromStaff ? '0 10px 10px 10px' : '10px 0 10px 10px',
                background: msg.fromStaff
                    ? 'rgba(88, 101, 242, 0.12)'
                    : 'rgba(255,255,255,0.06)',
                border: msg.fromStaff
                    ? '1px solid rgba(88, 101, 242, 0.3)'
                    : '1px solid rgba(255,255,255,0.1)',
                fontSize: '0.9rem',
                lineHeight: 1.55,
            }}>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: '0.2rem', opacity: 0.75 }}>
                    {msg.fromStaff ? 'MHA Staff' : username}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.message}</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.45, marginTop: '0.3rem', textAlign: 'right' }}>
                    {formatTime(msg.createdAt)}
                </div>
            </div>
        </div>
    );
};

export default App;